"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { HeroBody } from "./HeroBody";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

export function SpotlightHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const spotRef = useRef<HTMLDivElement | null>(null);
  const meshRef = useRef<HTMLDivElement | null>(null);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const root = rootRef.current;
    const spot = spotRef.current;
    if (!root) return;

    const ctx = gsap.context(() => {
      const intro = [badgeRef.current, titleRef.current, subtitleRef.current, ctasRef.current].filter(Boolean);
      if (reduced) {
        gsap.set(intro, { opacity: 1, y: 0 });
      } else {
        gsap.set(intro, { opacity: 0, y: 26 });
        gsap.to(intro, {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.11,
          delay: 0.1,
        });
        if (meshRef.current) {
          gsap.to(meshRef.current, {
            backgroundPosition: "100% 50%",
            duration: 18,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
          });
        }
      }
    }, rootRef);

    // Cursor-follow spotlight
    let raf = 0;
    let targetX = 0.5;
    let targetY = 0.35;
    let curX = targetX;
    let curY = targetY;

    function loop() {
      curX += (targetX - curX) * 0.08;
      curY += (targetY - curY) * 0.08;
      if (spot) {
        spot.style.background = `radial-gradient(circle 32rem at ${curX * 100}% ${curY * 100}%, rgba(56,189,248,0.35), transparent 60%)`;
      }
      raf = requestAnimationFrame(loop);
    }

    function onMove(e: PointerEvent) {
      const rect = root!.getBoundingClientRect();
      targetX = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      targetY = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    }

    if (!reduced) {
      raf = requestAnimationFrame(loop);
      root.addEventListener("pointermove", onMove);
    } else if (spot) {
      spot.style.background = `radial-gradient(circle 32rem at 50% 35%, rgba(56,189,248,0.28), transparent 60%)`;
    }

    return () => {
      ctx.revert();
      cancelAnimationFrame(raf);
      root.removeEventListener("pointermove", onMove);
    };
  }, [reduced]);

  return (
    <section
      ref={rootRef}
      className="hero-section hero-grain relative isolate overflow-hidden bg-[#0b1220] px-4 py-20 md:py-28"
      aria-label="Hero"
    >
      <div
        aria-hidden
        ref={meshRef}
        className="absolute inset-0 opacity-90"
        style={{
          backgroundImage:
            "radial-gradient(at 20% 20%, rgba(99,102,241,0.5) 0px, transparent 40%), radial-gradient(at 80% 30%, rgba(236,72,153,0.45) 0px, transparent 45%), radial-gradient(at 60% 90%, rgba(56,189,248,0.45) 0px, transparent 50%)",
          backgroundSize: "200% 200%",
          backgroundPosition: "0% 50%",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 80%)",
        }}
      />
      <div aria-hidden ref={spotRef} className="pointer-events-none absolute inset-0" />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, transparent 40%, rgba(11,18,32,0.85) 100%)" }}
      />

      <HeroBody
        content={content}
        primaryVariant="glow"
        secondaryVariant="outline"
        refs={{ badge: badgeRef, title: titleRef, subtitle: subtitleRef, ctas: ctasRef }}
      />
    </section>
  );
}
